import { useAxiosRequest } from "api/axiosRequest";
import { BASE_URL } from 'api/url_helper';

// PortAlert
export interface PortAlertBase {
  id?: string
  organizationId: string
  countryUnlocode: string
  port: string
  terminal: string
  berth: string
  description?: string
  createdBy?: string
  createdAt?: string
  updatedBy?: string
  updatedAt?: string
}
export interface PortAlert extends PortAlertBase {
  organization_name?: string
  country_name?: string
  riskLevel?: "red" | "green" | null
}

export interface UpdatePortAlertStruct {
  organizationId: string
  countryUnlocode: string
  port: string
  terminal: string
  berth: string
  description: string
  riskLevel: "red" | "green" | null
  id?: string
}

export type LocationType = 'country' | 'port' | 'terminal' | 'berth'

export interface LocationSelection {
  type: LocationType
  countryUnlocode: string
  countryName: string
  port?: string
  portUnlocode?: string
  terminal?: string
  terminalId?: string
  berth?: string
}

export interface BerthData {
  berth_id: string
  terminal_id?: string
  terminal_name?: string
  port_unloc?: string
  port_name?: string
}

export type PortAlerts = PortAlert[] | undefined

export interface CountryUnLocode {
  label: string
  value: string
}

export interface PortAlertWidget {
  port: string
  date_from: string
  riskLevel?: "red" | "yellow" | "green" | null
}

export type PortAlertWidgets = PortAlertWidget[] | null

const PortAlertService = () => {
  const request = useAxiosRequest()
  const getPortAlertsByOrgIDs = async (org_ids: string[] | undefined) => {
    try {
      if (!org_ids) return null
      const portAlertsUrl = `${BASE_URL}/portalerts`
      const response = await request<any, PortAlerts>(
        {
          url: portAlertsUrl,
          method: "GET",
          params: { org_id: org_ids },
          paramsSerializer: (params) =>
            Object.keys(params)
              .map((key) =>
                params[key]
                  .map((val: string) => `${key}=${encodeURIComponent(val)}`)
                  .join("&")
              )
              .join("&"),
        }
      );

      return response && response.data;
    } catch (error) {
      throw error;
    }
  };

  const createPortAlert = async (newPortAlert: PortAlert) => {
    try {
      const response = await request<any, PortAlert>(
        {
          url: `${BASE_URL}/portalert`,
          method: "POST",
          data: newPortAlert
        }
      );

      return response && response.data;
    } catch (error) {
      throw error;
    }
  };

  const updatePortAlert = async (updatedPortAlert: PortAlert) => {
    try {
      const response = await request<any, PortAlert>(
        {
          url: `${BASE_URL}/portalert`,
          method: "PUT",
          data: updatedPortAlert
        }
      );

      return response && response.data;
    } catch (error) {
      throw error;
    }
  };

  const deletePortAlert = async (id: string) => {
    try {
      const response = await request<any, PortAlert>(
        {
          url: `${BASE_URL}/portalert?id=${id}`,
          method: "DELETE",
        }
      );

      return response && response.data;
    } catch (error) {
      throw error;
    }
  };

  const getBerths = async () => {
    try {
      const response = await request<any, BerthData[]>({
        url: `${BASE_URL}/portalerts/berths`,
        method: "GET",
      });
      return response && response.data;
    } catch (error) {
      throw error;
    }
  };

  return {
    getPortAlertsByOrgIDs,
    createPortAlert,
    updatePortAlert,
    deletePortAlert,
    getBerths,
  }
}

export default PortAlertService
